/**
 * The cells the rule page's three tabs share (rv-architect I2): one rendering of a row's merchant,
 * amount and date in each `RuleRowTxn` state, so Applied to, Matches now and Removed cannot drift
 * apart in how they show a transaction that is loading, gone or failed to load.
 */
import { Button } from '../../platform/primitives/Button'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { MerchantAvatar } from '../../platform/primitives/MerchantAvatar'
import { Money } from '../../platform/primitives/Money'
import { shortDate } from '../transactions/txnDisplay'
import type { RuleRowTxn } from './useRuleRowTxns'

export const TXN_FAILED = "Couldn't load this transaction"
/** A transaction gone from the user's view: a 404, or a log whose embedded summary is `null`. */
export const TXN_GONE = 'This transaction is no longer available'

/** A row's merchant name for a control's label, with what to say while it is unread. */
export const rowName = (t: RuleRowTxn, fallback: string) => (t.status === 'found' ? t.merchant : fallback)

/**
 * The merchant, linked to the transaction once read. Before that, or once it is gone, the row's own
 * snapshot of the name (`snapshot`: a log entry's or a match's) with a plain avatar; a row with none
 * (an exclusion) shows "…" while loading. A gone one adds `TXN_GONE`. A failed read shows
 * nothing here: `Unavailable` beside it says so.
 */
export function MerchantCell({ id, t, snapshot }: { id: string; t: RuleRowTxn; snapshot: string | null }) {
  if (t.status === 'found') {
    return (
      <span className="rule-applied__merchant">
        <MerchantAvatar merchant={t.avatar} />
        <InlineLink to={`/transactions/${encodeURIComponent(t.id)}`} className="rule-applied__link">
          <span className="rule-applied__name">{t.merchant}</span>
        </InlineLink>
      </span>
    )
  }
  if (snapshot === null) {
    if (t.status === 'missing') return <span className="rule-applied__gone">{TXN_GONE}</span>
    return t.status === 'loading' ? <span className="rule-applied__muted">…</span> : null
  }
  return (
    <span className="rule-applied__merchant">
      <MerchantAvatar merchant={{ id, name: snapshot, type: 'other', is_global: false }} />
      <span className="rule-applied__name">{snapshot}</span>
      {t.status === 'missing' && <span className="rule-applied__gone">{TXN_GONE}</span>}
    </span>
  )
}

/** A read that failed for a reason other than a 404: said in words, with a retry, so the row never
 *  looks like one that is blank on purpose. */
export function Unavailable({ t }: { t: RuleRowTxn }) {
  if (t.status !== 'unavailable') return null
  return (
    <span className="rule-applied__failed">
      <span className="rule-applied__gone">{TXN_FAILED}</span>
      <Button variant="outline" size="panel" onClick={t.retry}>
        Try again
      </Button>
    </span>
  )
}

/**
 * The amount in the transaction's own currency and sign. `cents` overrides the transaction's amount
 * when the row carries its own (a match's); the currency always comes from the transaction, so a row
 * whose transaction is unread shows none rather than a guessed one.
 */
export function AmountCell({ t, cents }: { t: RuleRowTxn; cents?: number }) {
  if (t.status === 'found') return <Money cents={cents ?? t.amountCents} currency={t.currency} kind="flow" />
  return <span className="rule-applied__muted">{t.status === 'loading' ? '…' : '—'}</span>
}

export function DateCell({ t }: { t: RuleRowTxn }) {
  if (t.status === 'found') return <span className="rule-applied__muted">{shortDate(t.date)}</span>
  return <span className="rule-applied__muted">{t.status === 'loading' ? '…' : '—'}</span>
}
